import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import BackendAuthHandler from './BackendAuthHandler';

export const FIDES_CHAT_APP_NAME = 'fides-chat';
export const CONVERSATIONS_COLLECTION =
  process.env.REACT_APP_FIRESTORE_CONVERSATIONS_COLLECTION || 'conversations';
const APP_NAME = FIDES_CHAT_APP_NAME;

// Cached Firestore instance — valid for the lifetime of the Auth session.
// Firebase Auth SDK handles ID-token refresh automatically after sign-in;
// we only need a new custom token if currentUser is null (session lost).
let firestoreInstance: Firestore | null = null;
let signInPromise: Promise<Firestore> | null = null;

async function fetchFirebaseToken(): Promise<string> {
  const token = await new BackendAuthHandler().withAuthToken();

  if (!token) {
    throw new Error('Missing admin auth token');
  }

  const response = await fetch('/api/chat/firebase-token', {
    method: 'GET',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get Firebase token');
  }

  const data = (await response.json()) as { data?: { token?: string } };
  const customToken = data.data?.token;

  if (!customToken) {
    throw new Error('Firebase token missing from response');
  }

  return customToken;
}

// Public Firebase project config — not a secret, it's a project identifier that
// tells the SDK which project to connect to. It MUST match the project the
// backend's Admin SDK mints the chat custom token for, which differs per
// environment (staging-admin-ui in staging, investment-management-prod in
// production) — a mismatch makes signInWithCustomToken fail with
// auth/custom-token-mismatch. Supplied per-environment via
// REACT_APP_FIREBASE_CONFIG (a JSON blob); the staging values below are the
// fallback for local dev.
const STAGING_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDAv-n1MqiaD2ADX85z9_VSjFnI1HqQ4NU',
  appId: '1:542652041372:web:446084488e04f135890559',
  authDomain: 'staging-admin-ui.firebaseapp.com',
  messagingSenderId: '542652041372',
  projectId: 'staging-admin-ui',
  storageBucket: 'staging-admin-ui.firebasestorage.app',
};

function resolveFirebaseConfig(): Record<string, string> {
  const raw = process.env.REACT_APP_FIREBASE_CONFIG;
  if (!raw) {
    return STAGING_FIREBASE_CONFIG;
  }
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    if (parsed.projectId && parsed.apiKey && parsed.appId) {
      return parsed;
    }
    console.warn(
      'REACT_APP_FIREBASE_CONFIG missing projectId/apiKey/appId — falling back to staging config',
    );
  } catch {
    console.warn('REACT_APP_FIREBASE_CONFIG is not valid JSON — falling back to staging config');
  }
  return STAGING_FIREBASE_CONFIG;
}

function getChatFirebaseApp(): FirebaseApp {
  return (
    getApps().find((app) => app.name === APP_NAME) ??
    initializeApp(resolveFirebaseConfig(), APP_NAME)
  );
}

export function invalidateFirebaseToken() {
  firestoreInstance = null;
  signInPromise = null;
}

export async function getFidesChatFirestore(): Promise<Firestore> {
  const app = getChatFirebaseApp();
  const auth = getAuth(app);

  // Already signed in — Firebase Auth SDK handles ID-token refresh automatically.
  // Never fetch a new custom token while the session is alive.
  if (auth.currentUser) {
    return firestoreInstance ?? (firestoreInstance = getFirestore(app));
  }

  // Deduplicate concurrent sign-in calls.
  if (!signInPromise) {
    signInPromise = (async () => {
      try {
        const customToken = await fetchFirebaseToken();
        await signInWithCustomToken(auth, customToken);
        firestoreInstance = getFirestore(app);
        return firestoreInstance;
      } catch (error) {
        firestoreInstance = null;
        throw error;
      } finally {
        signInPromise = null;
      }
    })();
  }

  return signInPromise;
}

export const getFideschatFirestore = getFidesChatFirestore;
